import { z } from 'zod';
import { IdSchema } from './encoding.js';
/**
 * Accounts Receivable contracts (ERD §5.8; FRD-FIN-057 to FIN-064; FRD-VAT-021, VAT-026 to
 * VAT-030). Money is a decimal string at the amount scale (SDD §9.2). No revenue-recognition
 * method is represented (CLR-03).
 */
const AmountSchema = z
    .string()
    .regex(/^\d{1,17}(\.\d{1,2})?$/, 'must be a decimal amount with at most 2 decimals');
const BusinessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const SummarySchema = z.object({ id: IdSchema, summary: z.string().nullable() });
/** The retained VAT of a line, as determined at capture (FRD-VAT-021, VAT-024). */
const LineVatSchema = z
    .object({
    treatment: z.string(),
    taxCode: z.string().nullable(),
    rateApplied: z.string(),
    vatAmount: z.string(),
    registrationState: z.string(),
    adjustedDetailId: IdSchema.nullable(),
    adjustmentReference: z.string().nullable(),
})
    .nullable();
const LineDimensionsRequest = {
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    employeeId: IdSchema.optional(),
    assetId: IdSchema.optional(),
    locationId: IdSchema.optional(),
};
// ----------------------------------------------------------------------------- invoices
/** A line names the COA revenue account it posts to (FRD-FIN-059); never inferred. */
export const CustomerInvoiceLineRequestSchema = z.strictObject({
    description: z.string().trim().min(1).max(500),
    accountId: IdSchema,
    netAmount: AmountSchema,
    ...LineDimensionsRequest,
});
export const CustomerInvoiceRequestSchema = z.strictObject({
    clientId: IdSchema,
    organizationId: IdSchema,
    invoiceDate: BusinessDateSchema,
    taxDate: BusinessDateSchema,
    dueDate: BusinessDateSchema.optional(),
    /** The receivable line's cost centre, where the receivable account requires one (FIN-064). */
    costCenterId: IdSchema.optional(),
    description: z.string().max(500).optional(),
    lines: z.array(CustomerInvoiceLineRequestSchema).min(1).max(500),
});
export const CustomerInvoiceSchema = z.object({
    id: IdSchema,
    /** The tax-invoice number, allocated at issue (FRD-VAT-028); null on a draft. */
    documentNo: z.string().nullable(),
    clientId: IdSchema,
    clientLabel: z.string().nullable(),
    organizationId: IdSchema,
    invoiceDate: z.string(),
    taxDate: z.string(),
    dueDate: z.string().nullable(),
    costCenterId: IdSchema.nullable(),
    description: z.string().nullable(),
    status: z.string(),
    approvalStatus: z.string(),
    netTotal: z.string(),
    vatTotal: z.string(),
    /** Whether the VAT is charged: registered state only (CLR-22, FRD-VAT-026). */
    vatCharged: z.boolean(),
    grossTotal: z.string(),
    creditedAmount: z.string(),
    receivedAmount: z.string(),
    outstandingAmount: z.string(),
    /** unpaid, part_paid or paid, derived from receipts (FRD-FIN-057). */
    paymentStatus: z.string(),
    /** Outstanding after its due date (FRD-FIN-057). */
    overdue: z.boolean(),
    journalId: IdSchema.nullable(),
    issuedAt: z.string().nullable(),
    version: z.number().int(),
});
/** The issued tax-invoice snapshot (SDD §5.8, MD-12; FRD-VAT-027). */
export const TaxInvoiceSnapshotSchema = z.object({
    customerName: z.string(),
    customerAddressLine1: z.string().nullable(),
    customerAddressLine2: z.string().nullable(),
    customerCity: z.string().nullable(),
    customerCountry: z.string().nullable(),
    customerTrn: z.string().nullable(),
    issuerName: z.string(),
    issuerAddressLine1: z.string().nullable(),
    issuerAddressLine2: z.string().nullable(),
    issuerCity: z.string().nullable(),
    issuerCountry: z.string().nullable(),
    issuerTrn: z.string().nullable(),
    registrationState: z.string(),
    /** The document heading configured for the registration state (FRD-VAT-030). */
    heading: z.string(),
    /** The configured customer-facing note while TAQON is not registered (FRD-VAT-030). */
    note: z.string().nullable(),
});
export const CustomerInvoiceLineSchema = z.object({
    id: IdSchema,
    lineNo: z.number().int(),
    description: z.string(),
    accountId: IdSchema,
    accountCode: z.string().nullable(),
    accountName: z.string().nullable(),
    netAmount: z.string(),
    costCenterId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    employeeId: IdSchema.nullable(),
    assetId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    vat: LineVatSchema,
});
export const CustomerInvoiceDetailSchema = z.object({
    invoice: CustomerInvoiceSchema,
    client: SummarySchema,
    journal: SummarySchema.nullable(),
    lines: z.array(CustomerInvoiceLineSchema),
    snapshot: TaxInvoiceSnapshotSchema.nullable(),
    creditNotes: z.array(z.object({
        id: IdSchema,
        documentNo: z.string().nullable(),
        status: z.string(),
        creditNoteDate: z.string(),
        grossTotal: z.string(),
    })),
    receipts: z.array(z.object({
        receiptId: IdSchema,
        documentNo: z.string(),
        allocatedAmount: z.string(),
        allocationDate: z.string(),
        fromAdvance: z.boolean(),
    })),
});
// ------------------------------------------------------------------------- credit notes
export const CustomerCreditNoteRequestSchema = z.strictObject({
    customerInvoiceId: IdSchema,
    creditNoteDate: BusinessDateSchema,
    taxDate: BusinessDateSchema,
    reason: z.string().trim().min(1).max(500),
    lines: z
        .array(z.strictObject({
        description: z.string().trim().min(1).max(500),
        /** The revenue-adjustment account (FRD-FIN-060; COA 49101–49104). */
        accountId: IdSchema,
        netAmount: AmountSchema,
        creditedInvoiceLineId: IdSchema.optional(),
        ...LineDimensionsRequest,
    }))
        .min(1)
        .max(500),
});
export const CustomerCreditNoteSchema = z.object({
    id: IdSchema,
    documentNo: z.string().nullable(),
    customerInvoiceId: IdSchema,
    invoiceDocumentNo: z.string().nullable(),
    clientId: IdSchema,
    clientLabel: z.string().nullable(),
    creditNoteDate: z.string(),
    taxDate: z.string(),
    reason: z.string(),
    status: z.string(),
    netTotal: z.string(),
    vatTotal: z.string(),
    vatCharged: z.boolean(),
    grossTotal: z.string(),
    journalId: IdSchema.nullable(),
    issuedAt: z.string().nullable(),
    version: z.number().int(),
});
export const CustomerCreditNoteDetailSchema = z.object({
    creditNote: CustomerCreditNoteSchema,
    journal: SummarySchema.nullable(),
    lines: z.array(CustomerInvoiceLineSchema.extend({
        creditedInvoiceLine: z.object({ id: IdSchema, lineNo: z.number().int() }).nullable(),
    })),
});
// ----------------------------------------------------------------------------- receipts
const ReceiptAllocationRequestSchema = z.strictObject({
    customerInvoiceId: IdSchema,
    allocatedAmount: AmountSchema,
    allocationDate: BusinessDateSchema,
});
export const CustomerReceiptRequestSchema = z.strictObject({
    clientId: IdSchema,
    receiptDate: BusinessDateSchema,
    amount: AmountSchema,
    bankCashAccountId: IdSchema,
    /** Posting dimension not derivable from the client (ERD §5.8). */
    costCenterId: IdSchema.optional(),
    reference: z.string().max(200).optional(),
    description: z.string().max(500).optional(),
    allocations: z.array(ReceiptAllocationRequestSchema).max(200),
});
/** An allocation of a posted receipt's unallocated amount — a customer advance (FIN-063). */
export const ApplyReceiptAdvanceRequestSchema = ReceiptAllocationRequestSchema;
export const CustomerReceiptSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    clientId: IdSchema,
    clientLabel: z.string().nullable(),
    receiptDate: z.string(),
    amount: z.string(),
    allocatedAmount: z.string(),
    /** Held as a customer advance (FRD-FIN-063). */
    unallocatedAmount: z.string(),
    bankCashTransactionId: IdSchema,
    costCenterId: IdSchema.nullable(),
    status: z.string(),
    journalId: IdSchema.nullable(),
    reference: z.string().nullable(),
    description: z.string().nullable(),
    version: z.number().int(),
});
export const CustomerReceiptDetailSchema = z.object({
    receipt: CustomerReceiptSchema,
    register: z.object({ id: IdSchema, code: z.string(), name: z.string() }).nullable(),
    journal: SummarySchema.nullable(),
    allocations: z.array(z.object({
        id: IdSchema,
        customerInvoiceId: IdSchema,
        invoiceDocumentNo: z.string().nullable(),
        allocatedAmount: z.string(),
        allocationDate: z.string(),
        fromAdvance: z.boolean(),
        journalId: IdSchema.nullable(),
    })),
});
/** A supplier advance offset against a later invoice (FRD-FIN-078). */
export const ApplyPaymentAdvanceRequestSchema = z.strictObject({
    supplierInvoiceId: IdSchema,
    allocatedAmount: AmountSchema,
    allocationDate: BusinessDateSchema,
});
// ------------------------------------------------------------------------------ reports
/** The receivables of one customer (FRD-FIN-062, FIN-131). */
export const CustomerOutstandingRowSchema = z.object({
    clientId: IdSchema,
    clientLabel: z.string().nullable(),
    invoiced: z.string(),
    credited: z.string(),
    received: z.string(),
    outstanding: z.string(),
    overdue: z.string(),
    /** Unallocated receipts held as customer advances (FRD-FIN-063). */
    advances: z.string(),
    openInvoices: z.number().int(),
});
/** The payables of one supplier (FRD-FIN-077, FIN-132). */
export const SupplierOutstandingRowSchema = z.object({
    supplierId: IdSchema,
    supplierLabel: z.string().nullable(),
    invoiced: z.string(),
    credited: z.string(),
    paid: z.string(),
    outstanding: z.string(),
    overdue: z.string(),
    /** Unallocated payments held as supplier advances (FRD-FIN-078). */
    advances: z.string(),
    openInvoices: z.number().int(),
});
//# sourceMappingURL=receivables.js.map